import React from "react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/Primitives/Dropdown"
import { Text } from "@/components/Primitives/Text"
import { Icon } from "@/components/Primitives/Icon"
import { BookOpenIcon, ListBulletIcon } from "@heroicons/react/24/outline"
import { cn } from "@/lib/utils/utils"
import { useConfig } from "@/hooks/useConfig"

interface Stage {
  id: string
  stage: { title: string }
}

interface StageDropdownMenuProps {
  stages: Stage[]
  currentStageIndex: number
  completedStages: Set<string>
  onStageSelect?: (index: number) => void
  calculateStageProgress: (stageId: string) => number
  isStageNavigable: (index: number) => boolean
  journeyProgress: number
  forceOpen?: boolean
}

export const StageDropdownMenu: React.FC<
  StageDropdownMenuProps & React.HTMLAttributes<HTMLButtonElement>
> = ({
  stages,
  currentStageIndex,
  completedStages,
  onStageSelect,
  calculateStageProgress,
  isStageNavigable,
  journeyProgress,
  forceOpen = false,
}) => {
  const { tp } = useConfig()
  if (stages.length < 2) {
    return (
      <>
        <Text
          variant="small"
          className="text-purple-500 hidden md:block p-2"
          bold
        >
          {tp("config_journey_progress", {
            journeyProgress: journeyProgress.toFixed(0),
          })}
        </Text>
      </>
    )
  }
  return (
    <DropdownMenu open={forceOpen ? true : undefined} onOpenChange={() => {}}>
      <DropdownMenuTrigger asChild>
        <button className="px-2 py-2 w-10 h-10 md:w-fit md:gap-2 bg-purple-50 rounded-lg flex items-center justify-center">
          <Text
            variant="small"
            className="text-purple-500 hidden md:block"
            bold
          >
            {tp("config_journey_progress", {
              journeyProgress: journeyProgress.toFixed(0),
            })}
          </Text>
          <Icon icon={ListBulletIcon} size="sm" className="text-purple-700" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="p-4 mt-2 z-50 bg-white border rounded-lg shadow-lg w-[18.75rem]"
      >
        <div className="space-y-2">
          {stages.map((stage, index) => {
            const isCompleted = completedStages.has(stage.id)
            const progress = calculateStageProgress(stage.id)
            return (
              <DropdownMenuItem
                key={stage.id}
                data-testid={`stage-item-${index}`}
                disabled={!isStageNavigable(index)}
                className={cn(
                  "flex items-center justify-between rounded-lg",
                  isCompleted
                    ? "bg-transparent-500"
                    : index === currentStageIndex
                      ? "bg-transparent-50"
                      : "bg-transparent-50",
                  isStageNavigable(index)
                    ? "cursor-pointer"
                    : "opacity-50 filter grayscale cursor-not-allowed",
                )}
                onSelect={() => {
                  if (isStageNavigable(index)) {
                    onStageSelect?.(index)
                  }
                }}
              >
                <div className="flex items-center gap-3 mr-2">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center">
                    <Icon
                      icon={BookOpenIcon}
                      size="sm"
                      className="text-purple-500"
                    />
                  </div>
                  <div className="text-left">
                    <Text variant="caption" bold className="text-purple-500">
                      {tp("config_header_stage", {
                        index: `${index + 1}`,
                      })}
                    </Text>
                    <Text variant="small">{stage.stage.title}</Text>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-purple-500 rounded-full px-2 min-w-[3rem] justify-center">
                  {isCompleted ? (
                    <Text variant="small" className="text-purple-50">
                      100%
                    </Text>
                  ) : (
                    <Text variant="small" className="text-purple-50">
                      {`${progress}%`}
                    </Text>
                  )}
                </div>
              </DropdownMenuItem>
            )
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
